import uuidv4 from 'uuid/v4';
import _ from 'lodash';
import { roundToNearestMultiple, has } from '../../../utils.js';
import basic from './common/basic.js';
import rect from './common/rect.js';
import updater from '../updater.js';

/* State example:
state: {
  byId: {
    "comment_1": {
      "userId": 43,
      "content": "<p>What are you saying there?</p>",
      "createdAt": ...
      "updatedAt": ...
      "resoled": false,
      "children": ["comment_2"],
      "position": {
        id: "board_1",
        type: "boards",
        "x": 500,
        "y": 200,
      }
    },
    "comment_2": {
      "userId": 55,
      "content": "<p>Whatever</p>",
      "createdAt": ...
      "updatedAt": ...
    },
  }
}
*/
const NEW_COMMENT = {
  userId: null,
  content: null,
  createdAt: null,
  updatedAt: null,
};
const options = {};

const state = { byId: {} };

const getters = {
  ...basic.getters(options),
  ...rect.getters(options),

  /**
   * Returns all the threads that are either unresolved or anything if show resolved is not set
   */
  getAllThreads(state, getters) {
    return filterResolved => {
      return getters.getAllNodes.filter(comment => {
        // Filter out root
        if (has(comment, 'root')) return false;
        // Filter out replies
        if (!has(comment, 'children')) return false;
        // Filter out resolved threads (if asked)
        if (filterResolved && comment.resolved) return false;
        return true;
      });
    };
  },

  /**
   * Returns the last reply date for a thread
   * @param {string} id The thread id
   * @returns {string}  The createdAt date of the last reply (or the main thread)
   */
  getThreadLastReplyDate(state, getters) {
    return id => {
      const thread = getters.getNode(id);
      // Use thread' s date
      let date = thread.createdAt;
      // Unless thread has children, then use last reply's date
      if (thread.children && thread.children.length > 0) {
        date = getters.getNode(thread.children[thread.children.length - 1])
          .createdAt;
      }
      return date;
    };
  },
};

const actions = {
  ...basic.actions({
    type: 'comments',
    container: { type: 'comments', prop: 'children' },
  }),

  /**
   * Create a new default comment
   * @param   {Object}        props                 Any extra or updated properties
   * @param   {String|null}   tId                   The comment id (if null uses the root container)
   * @param   {Number}        tIndex                The index in the container
   * @param   {Boolean}       apply                 Whether to apply the patch after finishing
   * @returns {String}                              The id of the component that was created
   */
  customCreate(
    context,
    { props = {}, tId = null, tIndex = '-', apply = true }
  ) {
    // In case no parent container specified use root container
    tId = tId || context.getters.getRootContainer.id;

    const id = uuidv4();
    // Center the position
    if (has(props, 'position')) {
      const { position } = props;
      console.log('Given position is:', position);
      let x = position.x - 22 / 2;
      let y = position.y - 20 / 2;

      // Constrain the element position to never leave the board limits
      x = Math.max(x, 0);
      y = Math.max(y, 0);
      x = Math.min(x, 20000 - 22);
      y = Math.min(y, 20000 - 20);

      const { snap, size } = context.rootGetters['project/config/getGrid'];
      if (snap) {
        x = roundToNearestMultiple(x, size);
        y = roundToNearestMultiple(y, size);
      }
      // Overwrite x, y
      props.position = { ...props.position, x, y };
      console.log('Modified position:', props.position);
    }

    // Add the createdAt property (server time)
    props.createdAt = new Date(
      Date.now() - context.rootGetters.getServerTimeDiff
    );

    const node = { id, ...NEW_COMMENT, ...props };
    context.dispatch('create', { node, tId, tIndex, apply: false });

    // In case the comment is a full thread add a reference to it from the item that is positioned to
    if (has(props, 'children') && has(props, 'position')) {
      const { position } = props;
      const { id: rId, type: rType } = position;
      context.dispatch('createReferences', {
        ids: [id],
        rId,
        rType,
        rProp: 'comments',
        apply: false,
      });

      // Open the thread for editing
      context.dispatch(
        'project/config/comments/setOpen',
        { id, apply: false },
        { root: true }
      );
    }

    updater.complete({ apply });
    return Promise.resolve(id);
  },

  /**
   * Update some of the comment's properties
   * @param {String}  id    The comment id
   * @param {Object}  props Contains only the properties that are changed
   * @param {Boolean} apply Whether to apply the patch after finishing
   */
  customUpdate(context, { id, props, apply }) {
    context.dispatch('update', { id, props, apply: false });
    updater.complete({ apply });
  },

  /**
   * Remove a comment with specified id from the store
   * @param {String}  id      The comment id
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  customDelete(context, { id, apply }) {
    const comment = context.getters.getNode(id);
    // Find all descendants (children & subchildren)
    const ids = _.cloneDeep(
      context.getters.getDescendantNodes(id, 'comments', 'children')
    );
    console.log('Descendant nodes of', id, 'are:', ids);
    // Add the current comment
    console.log('Deleting multiple replies:', ids);
    // Delete the comment objects
    context.dispatch('deleteMany', { ids, apply: false });
    context.dispatch('delete', { id, apply: false });
    // Delete comment references, in case the comment is a thread and close it if it is open
    if (has(comment, 'children') && has(comment, 'position')) {
      // Delete thread references from boards
      context.dispatch('deleteReferences', { ids: [id], apply: false });

      // Close the thread if it is open
      const open = context.rootGetters['project/config/comments/getOpen'];
      if (open === id) {
        context.dispatch(
          'project/config/comments/setOpen',
          { id: null, apply: false },
          { root: true }
        );
      }

      // Deselect the thread
      context.dispatch(
        'project/config/comments/deselect',
        { id, apply: false },
        { root: true }
      );
    }
    updater.complete({ apply });
  },

  /**
   * Remove references to comments. If the property holding the reference is emptied it is deleted
   * @param {array}   ids     The ids of the comment references to be deleted
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  deleteReferences(context, { ids, apply }) {
    console.log('comments::deleteReferences ids:', ids);

    // Create the references object for each of the ids
    const references = ids.map(id => {
      const { position } = context.state.byId[id];
      const { id: rId, type: rType } = position;
      return { id, rId, rType };
    });

    // We need to group the references by rId
    const grouped = _.groupBy(references, 'rId');
    const patch = [];
    Object.values(grouped).forEach(refs => {
      const groupedIds = refs.map(ref => ref.id);
      const { rType, rId } = refs[0];
      // Find the object that has the reference
      const { comments } = context.rootState.project.data[rType].byId[rId];
      // If all the references in it are to be deleted, remove the property altogether
      if (comments.every(item => groupedIds.includes(item))) {
        patch.push({
          op: 'remove',
          path: `/data/${rType}/byId/${rId}/comments`,
        });
      } else {
        // Find and remove the references from the array backwards
        for (let index = comments.length - 1; index >= 0; index -= 1) {
          if (groupedIds.includes(comments[index])) {
            patch.push({
              op: 'remove',
              path: `/data/${rType}/byId/${rId}/comments/${index}`,
            });
          }
        }
      }
    });

    console.log('basic::deleteReference final patch:', patch);
    updater.add(patch);
    updater.complete({ apply });
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations: {},
  actions,
};

export default module;
